// What the account pages derive from the card account and its transactions (CardAccount/Transactions/CardSettings
// .cshtml(.cs)), and the queries they share.
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { cardApi, type CardAccount, type Transaction } from './api';

/** Loads and refunds add money; everything else takes it. */
export const isCredit = (transactionType: string) => transactionType === 'Load' || transactionType === 'Refund';

/** The latest posted load (CardAccountModel.LastReloadAt), as its ISO string, or null. */
export function lastReloadAt(transactions: readonly Transaction[]): string | null {
  let latest: { iso: string; t: number } | null = null;
  for (const tx of transactions) {
    if (tx.transactionType !== 'Load' || !tx.postedAt) continue;
    const t = Date.parse(tx.postedAt);
    if (!Number.isNaN(t) && (latest === null || t > latest.t)) latest = { iso: tx.postedAt, t };
  }
  return latest?.iso ?? null;
}

export const needsActivation = (account: CardAccount | null | undefined) => account?.cardStatus === 'IssuedInactive';
export const isFrozen = (account: CardAccount | null | undefined) =>
  account?.cardStatus === 'Suspended' || account?.cardStatus === 'Frozen';

/** Processors whose cards cannot be unfrozen from here (InterCash: only its account manager reinstates a card). */
export const NO_UNFREEZE = new Set(['InterCash']);

/**
 * W18: the badge on the card says what the card's status is. The legacy page had two words, "Active" and "Setup
 * needed" for everything else, so a frozen or closed card read as one still being set up.
 */
export function cardStatusBadge(
  cardStatus: string | null | undefined,
  provider: string,
): { label: string; tone: 'active' | 'pending' | 'frozen' | 'closed'; hint: string | null } {
  switch (cardStatus ?? '') {
    case 'Active':
      return { label: 'Active', tone: 'active', hint: null };
    case 'IssuedInactive':
      return {
        label: 'Setup needed',
        tone: 'pending',
        hint: 'Your card has been issued. Activate it in Card Settings to start using it.',
      };
    case 'Frozen':
    case 'Suspended':
      return {
        label: 'Locked',
        tone: 'frozen',
        hint: NO_UNFREEZE.has(provider)
          ? 'This card is locked and no purchases will go through. Call customer support to unlock it; if it is lost or stolen, report it in Card Settings to get a replacement.'
          : 'This card is locked and no purchases will go through. Unlock it in Card Settings when you are ready.',
      };
    case 'Closed':
      return {
        label: 'Closed',
        tone: 'closed',
        hint: 'This card is closed and can no longer be used. Contact support if you need a new one.',
      };
    case 'Rejected':
      return {
        label: 'Rejected',
        tone: 'closed',
        hint: 'Your card application was not approved. Contact support for details.',
      };
    case 'PendingOnboarding':
    case 'PendingApproval':
      return { label: 'Pending', tone: 'pending', hint: 'Your card application is being processed.' };
    default:
      return {
        label: 'Unknown',
        tone: 'pending',
        hint: 'We could not read the status of this card. Try again in a moment.',
      };
  }
}

const accountKey = (ref: string) => ['card', ref, 'account'] as const;

/** The card account (null when the API had none or failed) and, once connected, its transactions. */
export function useCardAccount(ref: string, withTransactions = false) {
  const client = useQueryClient();
  const [refreshed, setRefreshed] = useState(false);
  const account = useQuery({
    queryKey: accountKey(ref),
    queryFn: () => cardApi.account(ref),
    enabled: ref !== '',
    // the reference changes when a card is replaced: the page stays (with its message) while the new card is read
    placeholderData: keepPreviousData,
  });
  const cardReferenceId = account.data?.isConnected ? account.data.cardReferenceId : null;
  const transactions = useQuery({
    queryKey: ['card', ref, 'transactions', cardReferenceId],
    queryFn: () => cardApi.transactions(ref, cardReferenceId as string),
    enabled: withTransactions && !!cardReferenceId,
  });
  return {
    account: account.data ?? null,
    loaded: !account.isPending,
    transactions: transactions.data ?? [],
    /** "Refresh" (and after an action): read the account (and transactions) again. */
    refresh: () => {
      setRefreshed(true);
      return client.invalidateQueries({ queryKey: ['card', ref] });
    },
    /** The page was refreshed (a post-back in the legacy page): it no longer sends the user away. */
    refreshed,
    refreshing: account.isFetching || transactions.isFetching,
  };
}
